// @flow
import React, { useMemo, useState } from 'react'
import styles from './styles.css'
import type { TemplateNode } from 'lib/digital_file/model'

export const isAncestorOrSelf = (path: ?string, nodePath: string): boolean => {
  if (!path) return false
  return path === nodePath || path.startsWith(`${nodePath}|`)
}

type Props = {
  template: Array<TemplateNode>,
  expandPath: ?string,
  rowClassName: (path: string, node: TemplateNode, hasChildren: boolean) => string,
  renderRight: (path: string, node: TemplateNode, hasChildren: boolean) => any,
  onLeafClick: (path: string) => void,
}

const FolderTree = ({ template, expandPath, rowClassName, renderRight, onLeafClick }: Props) => {
  const initiallyExpanded = useMemo(() => {
    const out: { [path: string]: boolean } = {}
    const walk = (nodes, trail) => {
      for (const node of nodes) {
        const path = [...trail, node.slug].join('|')
        if (isAncestorOrSelf(expandPath, path) && node.children && node.children.length) {
          out[path] = true
        }
        if (node.children && node.children.length) {
          walk(node.children, [...trail, node.slug])
        }
      }
    }
    walk(template, [])
    return out
  }, [template, expandPath])

  const [expanded, setExpanded] = useState(initiallyExpanded)

  const toggle = (path: string) => {
    setExpanded((prev) => ({ ...prev, [path]: !prev[path] }))
  }

  const renderNode = (node: TemplateNode, trail: Array<string>, depth: number) => {
    const path = [...trail, node.slug].join('|')
    const hasChildren = node.children && node.children.length > 0
    const isOpen = !!expanded[path]
    const indentClass = depth === 1 ? styles.indent : depth >= 2 ? styles.indent2 : ''

    const onClick = () => {
      if (hasChildren) {
        toggle(path)
      } else {
        onLeafClick(path)
      }
    }

    return (
      <React.Fragment key={path}>
        <div className={`${styles.folder} ${indentClass} ${rowClassName(path, node, hasChildren)}`} onClick={onClick} role="button" tabIndex={0}>
          <span className={styles.folderLabel}>
            {hasChildren ? <span className={styles.chev}>{isOpen ? '▾' : '▸'}</span> : depth === 0 ? <span className={styles.chevSpacer} /> : null}
            {node.label}
          </span>
          {renderRight(path, node, hasChildren)}
        </div>
        {hasChildren && isOpen ? node.children.map((c) => renderNode(c, [...trail, node.slug], depth + 1)) : null}
      </React.Fragment>
    )
  }

  return <>{template.map((node) => renderNode(node, [], 0))}</>
}

export default FolderTree
